import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ServiceContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-blue);
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin-bottom: var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceSection = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  background: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FeatureTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  color: var(--accent-blue);
`;

const CaseStudiesSection = styled.section`
  margin-top: var(--spacing-xxl);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
`;

const CardContent = styled.div`
  padding: var(--spacing-lg);
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin: var(--spacing-sm) 0;
  color: var(--text);
`;

const CardDescription = styled.p`
  color: var(--text-light);
  margin-bottom: var(--spacing-md);
`;

const CardStat = styled.div`
  font-weight: 600;
  color: var(--accent-lavender);
  margin: var(--spacing-sm) 0;
`;

const CardLink = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

// Sample case studies specifically for Digital Tools
const caseStudies = [
  {
    id: 'itinerary-generator',
    title: 'Itinerary Generator',
    description: 'Custom tool development that dramatically cut planning time for multi-day tours.',
    image: '/images/projects/share_itinerary.webp',
    keyStat: 'Planning time reduced by 60%'
  },
  {
    id: 'fam-trip-planner',
    title: 'Fam Trip Connection Platform',
    description: 'A marketplace platform connecting travel agents with tour operators and hotels, creating an affordable alternative to expensive travel fairs.',
    image: '/images/projects/plan_fam_trips.webp',
    keyStat: 'Alternative to $5-10K travel fairs'
  },
  {
    id: 'marketing-plan-generator',
    title: 'Travel Marketing Plan Generator',
    description: 'An intelligent tool that guides travel businesses through creating comprehensive marketing plans, saving time and improving strategic planning.',
    image: '/images/projects/marketing-plan-generator.webp',
    keyStat: 'Saves 15+ hours per marketing plan'
  }
];

const DigitalToolsService = () => {
  return (
    <ServiceContainer>
      <Container>
        <PageTitle>Digital <span>Tools</span></PageTitle>
        <IntroText>
          Custom software solutions that save time and enhance customer experiences for travel businesses.
          Our Digital Tools are built specifically for the needs of small to medium travel operators.
        </IntroText>
        
        <ServiceSection>
          <h2>How We Help</h2>
          <p>
            From itinerary builders to marketing automation tools, we create accessible technology 
            that makes running your travel business more efficient without losing the human touch 
            that makes your offerings special.
          </p>
          
          <FeatureGrid>
            <Feature>
              <FeatureTitle>Itinerary Builders</FeatureTitle>
              <p>Custom tools that dramatically reduce planning time, improve consistency, and create professional client-facing documents.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Connection Platforms</FeatureTitle>
              <p>Specialized marketplaces and connection tools that help you forge new partnerships and reach new markets without costly trade shows.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Marketing Automation</FeatureTitle>
              <p>Time-saving tools that help you create strategic marketing plans, manage content, and maintain consistent messaging across channels.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Custom Solutions</FeatureTitle>
              <p>Bespoke digital tools designed for your specific business challenges, from inventory management to customer journey maps.</p>
            </Feature>
          </FeatureGrid>
        </ServiceSection>
        
        <CaseStudiesSection>
          <h2>Digital Tools Results</h2>
          <p>See how our custom digital tools have saved time, reduced costs, and improved experiences for travel businesses.</p>
          
          <CaseStudyGrid>
            {caseStudies.map(study => (
              <Card key={study.id}>
                <Link to={`/projects/${study.id}`}>
                  <CardImage style={{ backgroundImage: `url(${study.image})` }} />
                </Link>
                <CardContent>
                  <CardTitle>{study.title}</CardTitle>
                  <CardDescription>{study.description}</CardDescription>
                  <CardStat>{study.keyStat}</CardStat>
                  <CardLink to={`/projects/${study.id}`}>View Case Study</CardLink>
                </CardContent>
              </Card>
            ))}
          </CaseStudyGrid>
        </CaseStudiesSection>
        
        <CTASection>
          <CTATitle>Ready for Smarter Tools?</CTATitle>
          <p>Let's discuss how custom digital tools can help your travel business save time and enhance customer experiences.</p>
          <CTAButton to="/contact">Start Your Project</CTAButton>
        </CTASection>
      </Container>
    </ServiceContainer>
  );
};

export default DigitalToolsService; 